/**
 * Supabase client singleton + Clerk-aware hook.
 *
 * Reads `VITE_SUPABASE_URL` + `VITE_SUPABASE_ANON_KEY` from the validated
 * env (`src/lib/env.ts`). The client is only constructed when both values
 * are present; otherwise consumers will get a clear runtime error explaining
 * which env var is missing.
 *
 * ## Two export modes
 *
 * 1. `supabase` — static singleton that uses the anon key. Use for:
 *    - Public data queries (no RLS)
 *    - Edge Function calls (which use their own JWT)
 *    - Tests (mock at module boundary)
 *
 * 2. `useSupabaseClient()` — React hook that returns a Supabase client
 *    authenticated via the Clerk → Supabase token exchange bridge.
 *    Calls `POST /functions/v1/auth-exchange` to obtain an HS256 JWT
 *    that Supabase RLS can validate with `auth.uid()`.
 *    Token is cached in memory and refreshed 1 min before expiry.
 *
 * Spec: docs/architecture/00-planning/03-auth-strategy.md
 *       docs/stories/story-demo-1-auth-foundation.md
 */
import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import { useSession } from "@clerk/clerk-react";
import { useMemo, useState, useEffect } from "react";
import { loadEnv } from "@/lib/env";
import type { Database } from "@/types/supabase";

let client: SupabaseClient<Database> | undefined;

export function getSupabaseClient(): SupabaseClient<Database> {
  if (client) return client;
  const env = loadEnv();
  if (!env.VITE_SUPABASE_URL || !env.VITE_SUPABASE_ANON_KEY) {
    throw new Error(
      "Supabase client requested but VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY are not configured.",
    );
  }
  client = createClient<Database>(env.VITE_SUPABASE_URL, env.VITE_SUPABASE_ANON_KEY, {
    auth: {
      persistSession: false,
      autoRefreshToken: false,
    },
  });
  return client;
}

/**
 * Convenience proxy — most call sites can `import { supabase } from "@/lib/supabase"`.
 * The underlying client is lazily constructed on first property access so tests
 * can mock the module before the env is loaded.
 */
export const supabase: SupabaseClient<Database> = new Proxy(
  {} as SupabaseClient<Database>,
  {
    get(_target, prop, receiver) {
      const real = getSupabaseClient();
      const value = Reflect.get(real as object, prop, receiver);
      return typeof value === "function" ? value.bind(real) : value;
    },
  },
);

// ─── Token exchange cache ─────────────────────────────────────────────────────
// Keyed by Clerk user ID. Tokens are reused until 1 minute before expiry.

interface CachedToken {
  token: string;
  expiresAt: number; // epoch ms
}

const tokenCache = new Map<string, CachedToken>();

// In-flight Promise dedup: N components mounting simultaneously see cache miss
// before the first populates it, causing N parallel auth-exchange POSTs.
// Reusing the in-flight Promise per userId collapses them to 1.
const inflightTokenFetch = new Map<string, Promise<CachedToken | null>>();

const REFRESH_BUFFER_MS = 60_000; // refresh 1 min before expiry

// Retry transient network failures (e.g. brief offline, DNS blip, cold edge fn).
// 4xx/5xx responses are NOT retried — those indicate auth issues, not transport.
const AUTH_EXCHANGE_MAX_ATTEMPTS = 3;
const AUTH_EXCHANGE_BACKOFF_MS = [0, 400, 1200];

async function fetchSupabaseToken(
  clerkToken: string,
  supabaseUrl: string,
): Promise<{ token: string; expiresAt: number } | null> {
  const functionUrl = `${supabaseUrl}/functions/v1/auth-exchange`;
  let lastError: unknown = null;
  for (let attempt = 0; attempt < AUTH_EXCHANGE_MAX_ATTEMPTS; attempt++) {
    if (AUTH_EXCHANGE_BACKOFF_MS[attempt] > 0) {
      await new Promise((r) => setTimeout(r, AUTH_EXCHANGE_BACKOFF_MS[attempt]));
    }
    try {
      const resp = await fetch(functionUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${clerkToken}`,
        },
      });
      if (!resp.ok) {
        console.error("[supabase] auth-exchange failed:", resp.status, await resp.text());
        return null;
      }
      const data = (await resp.json()) as {
        supabase_token: string;
        user_id: string;
        expires_at: number;
      };
      return { token: data.supabase_token, expiresAt: data.expires_at };
    } catch (err) {
      lastError = err;
      // network/transport error — retry
    }
  }
  console.warn(
    `[supabase] auth-exchange transport failed after ${AUTH_EXCHANGE_MAX_ATTEMPTS} attempts:`,
    lastError,
  );
  return null;
}

/**
 * React hook that returns a Clerk-JWT-aware Supabase client.
 *
 * Uses the auth-exchange Edge Function to obtain an HS256 JWT signed with
 * the Supabase JWT secret. This token is forwarded in every request so
 * Supabase RLS can evaluate `auth.uid()` correctly.
 *
 * Token is cached in memory per Clerk user and refreshed 1 min before expiry.
 *
 * Use this hook in any component that makes RLS-gated queries.
 * Do NOT use in Edge Functions — they handle their own auth.
 */
export interface SupabaseAuth {
  client: SupabaseClient<Database>;
  isReady: boolean;
}

export function useSupabaseClient(): SupabaseAuth {
  const { session } = useSession();
  const env = loadEnv();
  const [supabaseToken, setSupabaseToken] = useState<string | null>(null);

  useEffect(() => {
    if (!session || !session.user?.id || !env.VITE_SUPABASE_URL) {
      setSupabaseToken(null);
      return;
    }

    let cancelled = false;
    const userId = session.user.id;

    async function refresh(): Promise<void> {
      // Check in-memory cache first
      const cached = tokenCache.get(userId);
      if (cached && cached.expiresAt > Date.now() + REFRESH_BUFFER_MS) {
        if (!cancelled) setSupabaseToken(cached.token);
        return;
      }

      // Dedup: reuse in-flight Promise if another component already started
      // the Clerk → auth-exchange roundtrip for this user.
      let inflight = inflightTokenFetch.get(userId);
      if (!inflight) {
        inflight = (async (): Promise<CachedToken | null> => {
          try {
            const clerkToken = await session.getToken();
            if (!clerkToken) return null;
            const result = await fetchSupabaseToken(clerkToken, env.VITE_SUPABASE_URL!);
            if (!result) return null;
            tokenCache.set(userId, result);
            return result;
          } catch (err) {
            console.error("[supabase] inflight token fetch error:", err);
            return null;
          }
        })().finally(() => {
          inflightTokenFetch.delete(userId);
        });
        inflightTokenFetch.set(userId, inflight);
      }

      const result = await inflight;
      if (!result || cancelled) return;
      setSupabaseToken(result.token);
    }

    void refresh();
    return () => {
      cancelled = true;
    };
  }, [session, env.VITE_SUPABASE_URL]);

  const client = useMemo(() => {
    if (!env.VITE_SUPABASE_URL || !env.VITE_SUPABASE_ANON_KEY) {
      // Warn but don't crash — isReady=false prevents any real queries from running.
      // Typical cause: .env not configured or dev server started before values were set.
      console.warn(
        "[useSupabaseClient] VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY are not configured. " +
          "Supabase features disabled. Set these in your .env and restart the dev server.",
      );
      return createClient<Database>(
        "https://placeholder.supabase.co",
        "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJyb2xlIjoiYW5vbiJ9.placeholder",
        { auth: { persistSession: false, autoRefreshToken: false } },
      );
    }
    return createClient<Database>(
      env.VITE_SUPABASE_URL,
      env.VITE_SUPABASE_ANON_KEY,
      {
        auth: {
          persistSession: false,
          autoRefreshToken: false,
        },
        global: {
          fetch: async (url: RequestInfo | URL, options: RequestInit = {}) => {
            const headers = new Headers(options.headers);
            if (supabaseToken) {
              headers.set("Authorization", `Bearer ${supabaseToken}`);
            }
            return fetch(url, { ...options, headers });
          },
        },
      },
    );
  }, [supabaseToken, env.VITE_SUPABASE_URL, env.VITE_SUPABASE_ANON_KEY]);

  return useMemo(
    () => ({ client, isReady: supabaseToken !== null }),
    [client, supabaseToken],
  );
}

/**
 * Returns the current Supabase JWT for the signed-in user (or null if not
 * yet exchanged). Use only for paths that need a raw token to call Edge
 * Functions directly — most code should use `useSupabaseClient()` instead.
 */
export function useSupabaseToken(): string | null {
  const { isReady, client } = useSupabaseClient();
  // The token lives inside the per-user cache; re-derive from the Clerk
  // session id we have available on the client.
  const { session } = useSession();
  if (!isReady || !session?.user?.id) return null;
  void client; // keep client referenced so the exchange runs
  const cached = tokenCache.get(session.user.id);
  return cached?.token ?? null;
}

/** Test-only — reset the singleton between cases. */
export const __supabaseTesting__ = {
  reset(): void {
    client = undefined;
  },
};
